import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Clock, RefreshCw, ChevronUp, ChevronDown, ChevronsUpDown,
  Loader2, ChevronLeft, ChevronRight, Activity, Eye,
  X, Copy, CheckCheck, Printer, AlertTriangle, CheckCircle, AlertCircle, PauseCircle,
  Bot, Square, CheckSquare, Minus, ShieldCheck, ListFilter, CalendarClock, Calendar,
} from 'lucide-react';
import axios from 'axios';
import { interspireApi } from '../services/api';
import { EmailQueueRow, StaggerResult, PostfixCompactStats } from '../types';
import { GUEST_TOKEN_KEY, useAuth } from '../context/AuthContext';

const PAGE_SIZE = 20;

interface AiRuleResult { rule: string; passed: boolean; note: string; evidence?: string[]; }
interface AiReview { jobid: number; passed: boolean; results: AiRuleResult[]; model: string; ruleVersion: string; reviewedAt: string; overridden?: boolean; overrideNote?: string; }

type SortKey = 'campaignName' | 'listName' | 'segmentName' | 'createdDate' | 'scheduledDate' | 'status';
type SortDir = 'asc' | 'desc';

// Re-use same health check helpers from Sends
interface HealthCheckRow { countyState: string; count: number; }
interface HealthCheckResult { segmentName: string; listId: number; breakdown: HealthCheckRow[]; total: number; }

function escapeHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function formatUnixDate(ts: number | null): string {
  if (ts == null || ts === 0) return '—';
  return new Date(ts * 1000).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

/** Browser-local value for <input type="datetime-local" /> from unix seconds. */
function toDatetimeLocalValue(ts: number): string {
  const d = new Date(ts * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): number {
  return Math.floor(new Date(value).getTime() / 1000);
}

function AiStatusIcon({ review, running }: { review: AiReview | undefined; running: boolean }) {
  if (running) return <Loader2 className="h-4 w-4 animate-spin text-indigo-400" />;
  if (!review) return <Square className="h-4 w-4 text-gray-300 dark:text-slate-600" />;
  if (review.overridden) return <ShieldCheck className="h-4 w-4 text-teal-500" />;
  if (review.passed) return <CheckCircle className="h-4 w-4 text-green-500" />;
  return <AlertCircle className="h-4 w-4 text-red-500" />;
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronsUpDown className="h-3.5 w-3.5 ml-1 text-gray-400 inline" />;
  return sortDir === 'asc'
    ? <ChevronUp className="h-3.5 w-3.5 ml-1 text-blue-500 inline" />
    : <ChevronDown className="h-3.5 w-3.5 ml-1 text-blue-500 inline" />;
}

function StatusBadge({ status }: { status: EmailQueueRow['status'] }) {
  if (status === 'sending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
      <CheckCircle className="h-3 w-3" /> Sending
    </span>
  );
  if (status === 'paused') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
      <PauseCircle className="h-3 w-3" /> Paused
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
      <Clock className="h-3 w-3" /> Scheduled
    </span>
  );
}

type PillColor = 'green' | 'yellow' | 'red' | 'gray';

function formatRatePerHour(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k/hr`;
  return `${n}/hr`;
}

function queuePillColor(count: number): PillColor {
  if (count > 10000) return 'red';
  if (count >= 2500) return 'yellow';
  return 'green';
}

function ratePillColor(rate: number, activeJobs: number): PillColor {
  if (activeJobs === 0) return 'gray';
  if (rate >= 12500) return 'green';
  if (rate >= 8000) return 'yellow';
  return 'red';
}

function errorsPillColor(count: number): PillColor {
  return count > 0 ? 'red' : 'green';
}

const PILL_CLASSES: Record<PillColor, string> = {
  green: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  red: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  gray: 'bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-300',
};

function MailOpsPill({ label, value, color, tooltip }: {
  label: string; value: string; color: PillColor; tooltip: string;
}) {
  return (
    <span title={tooltip} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${PILL_CLASSES[color]}`}>
      {label}: {value}
    </span>
  );
}

export default function Queue() {
  const { canAccess, user } = useAuth();
  const [rows, setRows]           = useState<EmailQueueRow[]>([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [cacheTtlMins, setCacheTtlMins] = useState<number>(60);

  const [sortKey, setSortKey] = useState<SortKey>('scheduledDate');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage]       = useState(1);
  const [search, setSearch]   = useState('');
  const handleSearch = (val: string) => { setSearch(val); setPage(1); };

  type ReviewFilter = 'all' | 'not_run' | 'passed' | 'failed' | 'overridden';
  const [reviewFilter, setReviewFilter] = useState<ReviewFilter>('all');
  const handleReviewFilter = (val: ReviewFilter) => { setReviewFilter(val); setPage(1); };

  // Health Check modal
  // If rendering inside GuestView, carry the token to new-tab pages via ?gt=
  const guestToken = useMemo(() => sessionStorage.getItem(GUEST_TOKEN_KEY), []);
  const gt = guestToken ? `?gt=${encodeURIComponent(guestToken)}` : '';

  const [healthRow, setHealthRow]         = useState<EmailQueueRow | null>(null);
  const [healthData, setHealthData]       = useState<HealthCheckResult | null>(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [healthCopied, setHealthCopied]   = useState(false);

  // AI Review state
  const [aiReviews, setAiReviews]           = useState<Record<number, AiReview>>({});
  const [selectedJobids, setSelectedJobids] = useState<Set<number>>(new Set());
  const [aiModalReview, setAiModalReview]   = useState<AiReview | null>(null);

  // Run progress (polling)
  interface RunProgress {
    runId: string;
    total: number;
    completed: number;
    failed: number;
    status: 'running' | 'done';
    errors: { jobid: number; error: string }[];
  }
  const [aiProgress, setAiProgress]   = useState<RunProgress | null>(null);
  const [aiStartError, setAiStartError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Override state (in modal)
  const [overrideChecked, setOverrideChecked]   = useState(false);
  const [overrideNote, setOverrideNote]         = useState('');
  const [overrideSaving, setOverrideSaving]     = useState(false);
  const [overrideError, setOverrideError]       = useState<string | null>(null);

  // Re-run All confirmation modal
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [rerunConfirmText, setRerunConfirmText] = useState('');

  // Schedule stagger modal (admin)
  const [showStaggerModal, setShowStaggerModal] = useState(false);
  const [staggerPreview, setStaggerPreview] = useState<StaggerResult | null>(null);
  const [staggerSelected, setStaggerSelected] = useState<Set<number>>(new Set());
  const [staggerLoading, setStaggerLoading] = useState(false);
  const [staggerApplying, setStaggerApplying] = useState(false);
  const [staggerError, setStaggerError] = useState<string | null>(null);

  // Single-job reschedule modal
  const [rescheduleRow, setRescheduleRow] = useState<EmailQueueRow | null>(null);
  const [rescheduleValue, setRescheduleValue] = useState('');
  const [rescheduleSaving, setRescheduleSaving] = useState(false);
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);

  // Clone modal
  const [cloneRow, setCloneRow] = useState<EmailQueueRow | null>(null);
  const [cloneName, setCloneName] = useState('');
  const [cloneWhen, setCloneWhen] = useState('');
  const [cloneSaving, setCloneSaving] = useState(false);
  const [cloneError, setCloneError] = useState<string | null>(null);

  // Mail Ops compact stats
  const [mailOpsStats, setMailOpsStats] = useState<PostfixCompactStats | null>(null);

  const fetchMailOps = useCallback(async () => {
    if (!canAccess('metricsLogs')) return;
    try {
      const res = await axios.get<PostfixCompactStats>('/api/metrics/postfix/compact');
      setMailOpsStats(res.data);
    } catch (e: any) {
      // On permission revoked mid-session, clear stale data and stop showing the strip
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        setMailOpsStats(null);
      }
      // Other errors (network, 500): keep last value — non-critical
    }
  }, [canAccess]);

  useEffect(() => {
    if (!canAccess('metricsLogs')) return;
    fetchMailOps();
    const id = setInterval(fetchMailOps, 60000);
    return () => clearInterval(id);
  }, [canAccess, fetchMailOps]);

  const openStaggerPreview = async () => {
    setStaggerLoading(true);
    setStaggerError(null);
    try {
      const res = await interspireApi.previewScheduleStagger();
      setStaggerPreview(res.data);
      setStaggerSelected(new Set(res.data.changes.map((c) => c.jobid)));
      setShowStaggerModal(true);
    } catch (err: any) {
      setStaggerError(err?.response?.data?.error ?? err?.message ?? 'Failed to load stagger preview');
    } finally {
      setStaggerLoading(false);
    }
  };

  const applyStaggerSelected = async () => {
    if (staggerSelected.size === 0) return;
    setStaggerApplying(true);
    setStaggerError(null);
    try {
      const res = await interspireApi.applyScheduleStagger(Array.from(staggerSelected));
      setShowStaggerModal(false);
      setStaggerPreview(null);
      await loadQueue(true);
      if (res.data.warnings.length) {
        window.alert(`Stagger applied to ${res.data.jobsUpdated} job(s).\n\nWarnings:\n${res.data.warnings.join('\n')}`);
      }
    } catch (err: any) {
      setStaggerError(err?.response?.data?.error ?? err?.message ?? 'Failed to apply stagger');
    } finally {
      setStaggerApplying(false);
    }
  };

  const loadQueue = useCallback(async (forceRefresh = false, opts?: { preservePage?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const res = await interspireApi.getEmailQueue(forceRefresh);
      const newRows = res.data.rows;
      setRows(newRows);
      setCacheTtlMins(res.data.cacheTtlMins ?? 60);
      setFetchedAt(Date.now());
      if (opts?.preservePage) {
        setPage((p) => {
          const tp = Math.max(1, Math.ceil(newRows.length / PAGE_SIZE));
          return Math.min(p, tp);
        });
      } else {
        setPage(1);
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? err?.message ?? 'Failed to load email queue');
    } finally {
      setLoading(false);
    }
  }, []);

  const canReschedule =
    canAccess('queueEditEmail') || canAccess('queueStaggerButton');

  const openReschedule = (row: EmailQueueRow) => {
    if (row.status !== 'scheduled' || row.scheduledDate == null) return;
    const now = Math.floor(Date.now() / 1000);
    if (row.scheduledDate <= now) return;
    setRescheduleRow(row);
    setRescheduleValue(toDatetimeLocalValue(row.scheduledDate));
    setRescheduleError(null);
  };

  const closeReschedule = () => {
    if (rescheduleSaving) return;
    setRescheduleRow(null);
    setRescheduleError(null);
  };

  const saveReschedule = async () => {
    if (!rescheduleRow || !rescheduleValue) return;
    const scheduledUnix = fromDatetimeLocalValue(rescheduleValue);
    if (!Number.isFinite(scheduledUnix)) {
      setRescheduleError('Enter a valid date and time.');
      return;
    }
    if (scheduledUnix <= Math.floor(Date.now() / 1000)) {
      setRescheduleError('Schedule time must be in the future.');
      return;
    }
    setRescheduleSaving(true);
    setRescheduleError(null);
    try {
      await interspireApi.rescheduleQueueJob(rescheduleRow.jobid, scheduledUnix);
      setRescheduleRow(null);
      await loadQueue(true, { preservePage: true });
    } catch (err: any) {
      setRescheduleError(
        err?.response?.data?.error ?? err?.message ?? 'Failed to reschedule'
      );
    } finally {
      setRescheduleSaving(false);
    }
  };

  const canClone = canAccess('queueEditEmail') || canAccess('queueStaggerButton');

  const openClone = (row: EmailQueueRow) => {
    setCloneRow(row);
    setCloneName(row.campaignName ? `${row.campaignName} (copy)` : 'Campaign (copy)');
    const base =
      row.scheduledDate != null && row.scheduledDate > Math.floor(Date.now() / 1000)
        ? row.scheduledDate
        : Math.floor(Date.now() / 1000) + 3600;
    setCloneWhen(toDatetimeLocalValue(base));
    setCloneError(null);
  };

  const closeClone = () => {
    if (cloneSaving) return;
    setCloneRow(null);
    setCloneError(null);
  };

  const saveClone = async () => {
    if (!cloneRow) return;
    const campaignName = cloneName.trim();
    if (!campaignName) {
      setCloneError('Enter a campaign name.');
      return;
    }
    if (!cloneWhen) {
      setCloneError('Enter a schedule date and time.');
      return;
    }
    const scheduledUnix = fromDatetimeLocalValue(cloneWhen);
    if (!Number.isFinite(scheduledUnix)) {
      setCloneError('Enter a valid date and time.');
      return;
    }
    if (scheduledUnix <= Math.floor(Date.now() / 1000)) {
      setCloneError('Schedule time must be in the future.');
      return;
    }
    setCloneSaving(true);
    setCloneError(null);
    try {
      await interspireApi.cloneQueueJob(cloneRow.jobid, { campaignName, scheduledUnix });
      setCloneRow(null);
      await loadQueue(true, { preservePage: true });
    } catch (err: any) {
      setCloneError(err?.response?.data?.error ?? err?.message ?? 'Failed to clone');
    } finally {
      setCloneSaving(false);
    }
  };

  useEffect(() => { loadQueue(); }, [loadQueue]);

  // Load AI reviews for the currently visible jobs whenever rows change
  useEffect(() => {
    if (rows.length === 0) return;
    const jobids = rows.map((r) => r.jobid);
    axios.get<AiReview[]>(`/api/ai/reviews?jobids=${jobids.join(',')}`)
      .then((res) => {
        const map: Record<number, AiReview> = {};
        res.data.forEach((rv) => { map[rv.jobid] = rv; });
        setAiReviews(map);
      })
      .catch(() => { /* silently ignore — reviews are optional */ });
  }, [rows]);

  // Stop polling on unmount
  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const startPolling = useCallback((runId: string) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.get<any>(`/api/ai/reviews/run/${runId}/status`);
        const run = res.data;

        setAiProgress({
          runId: run.runId,
          total: run.total,
          completed: run.completed,
          failed: run.failed,
          status: run.status,
          errors: run.errors ?? [],
        });

        // Merge any newly completed reviews into the map
        if (run.completedReviews?.length > 0) {
          setAiReviews((prev) => {
            const map = { ...prev };
            run.completedReviews.forEach((rv: AiReview) => { map[rv.jobid] = rv; });
            return map;
          });
        }

        if (run.status === 'done') {
          clearInterval(pollRef.current!);
          pollRef.current = null;
          setSelectedJobids(new Set());
        }
      } catch {
        clearInterval(pollRef.current!);
        pollRef.current = null;
      }
    }, 3000);
  }, []);

  const runAiReviews = useCallback(async (mode: 'new' | 'checked' | 'all') => {
    let jobids: number[];
    if (mode === 'all') {
      jobids = rows.map((r) => r.jobid);
    } else if (mode === 'checked') {
      jobids = Array.from(selectedJobids);
    } else {
      jobids = rows.filter((r) => !aiReviews[r.jobid]).map((r) => r.jobid);
    }
    if (jobids.length === 0) return;

    setAiStartError(null);
    setAiProgress({ runId: '', total: jobids.length, completed: 0, failed: 0, status: 'running', errors: [] });

    try {
      const res = await axios.post<{ runId: string; total: number }>('/api/ai/reviews/run', { jobids });
      setAiProgress((p) => p ? { ...p, runId: res.data.runId } : null);
      startPolling(res.data.runId);
    } catch (err: any) {
      setAiProgress(null);
      setAiStartError(err?.response?.data?.error ?? 'Failed to start review run');
    }
  }, [rows, selectedJobids, aiReviews, startPolling]);

  const handleOverrideSave = useCallback(async () => {
    if (!aiModalReview) return;
    setOverrideSaving(true);
    setOverrideError(null);
    try {
      const res = await axios.put<AiReview>(`/api/ai/reviews/${aiModalReview.jobid}/override`, {
        overridden: overrideChecked,
        overrideNote: overrideNote.trim() || null,
      });
      const updated = res.data;
      setAiReviews((prev) => ({ ...prev, [updated.jobid]: updated }));
      setAiModalReview(updated);
    } catch (err: any) {
      setOverrideError(err?.response?.data?.error ?? 'Failed to save override');
    } finally {
      setOverrideSaving(false);
    }
  }, [aiModalReview, overrideChecked, overrideNote]);

  const toggleSelectJob = (jobid: number) => {
    setSelectedJobids((prev) => {
      const next = new Set(prev);
      if (next.has(jobid)) next.delete(jobid); else next.add(jobid);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedJobids.size === paginated.length) {
      setSelectedJobids(new Set());
    } else {
      setSelectedJobids(new Set(paginated.map((r) => r.jobid)));
    }
  };

  const openHealthCheck = useCallback(async (row: EmailQueueRow) => {
    if (row.segmentId == null) return;
    setHealthRow(row);
    setHealthData(null);
    setHealthLoading(true);
    try {
      const res = await axios.get(`/api/interspire/segments/${row.segmentId}/health`);
      setHealthData(res.data);
    } catch {
      setHealthRow(null);
    } finally {
      setHealthLoading(false);
    }
  }, []);

  const closeHealthCheck = () => { setHealthRow(null); setHealthData(null); setHealthCopied(false); };

  const copyHealthCheck = () => {
    if (!healthData) return;
    const lines = [
      `Segment Health Check: ${healthData.segmentName}`,
      `List ID: ${healthData.listId}`,
      '',
      'County, State\tActive Contacts',
      ...healthData.breakdown.map(r => `${r.countyState}\t${r.count.toLocaleString()}`),
      '',
      `TOTAL\t${healthData.total.toLocaleString()}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setHealthCopied(true);
    setTimeout(() => setHealthCopied(false), 2500);
  };

  const printHealthCheck = () => {
    if (!healthData) return;
    const tbodyRows = healthData.breakdown
      .map(r => `<tr><td style="padding:3px 14px;border-bottom:1px solid #e5e7eb">${escapeHtml(r.countyState)}</td><td style="padding:3px 14px;text-align:right;border-bottom:1px solid #e5e7eb;font-weight:500">${r.count.toLocaleString()}</td></tr>`)
      .join('');
    const loc = healthData.breakdown.length;
    const html = `<!DOCTYPE html><html><head><title>Segment Health Check – ${healthData.segmentName}</title>
<style>
  body{font-family:Arial,sans-serif;margin:40px;color:#111}h1{font-size:18px;margin:0 0 4px}
  .meta{font-size:13px;color:#555;margin:0 0 20px}table{width:100%;border-collapse:collapse;font-size:14px}
  thead th{background:#f3f4f6;padding:7px 14px;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;border-bottom:2px solid #d1d5db}
  thead th:last-child{text-align:right}tfoot td{padding:8px 14px;font-weight:700;border-top:2px solid #374151;background:#f3f4f6;font-size:14px}tfoot td:last-child{text-align:right}
</style></head><body>
  <h1>Segment Health Check</h1>
  <p class="meta">${escapeHtml(healthData.segmentName)}<br>List ID: ${healthData.listId} &nbsp;·&nbsp; ${loc} location${loc !== 1 ? 's' : ''}</p>
  <table><thead><tr><th>County, State</th><th style="text-align:right">Active Contacts</th></tr></thead>
  <tbody>${tbodyRows}</tbody>
  <tfoot><tr><td>TOTAL</td><td>${healthData.total.toLocaleString()}</td></tr></tfoot></table>
</body></html>`;
    const w = window.open('', '_blank', 'width=720,height=900');
    if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };

  const filtered = rows.filter(r => {
    if (search.trim() && !r.campaignName?.toLowerCase().includes(search.trim().toLowerCase())) return false;
    if (reviewFilter !== 'all') {
      const rv = aiReviews[r.jobid];
      if (reviewFilter === 'not_run')   return !rv;
      if (reviewFilter === 'overridden') return !!rv?.overridden;
      if (reviewFilter === 'passed')    return !!(rv && rv.passed && !rv.overridden);
      if (reviewFilter === 'failed')    return !!(rv && !rv.passed && !rv.overridden);
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: any, bv: any;
    if (sortKey === 'scheduledDate') { av = a.scheduledDate ?? 0; bv = b.scheduledDate ?? 0; }
    else if (sortKey === 'createdDate') { av = a.createdDate ?? 0; bv = b.createdDate ?? 0; }
    else if (sortKey === 'status') { av = a.status; bv = b.status; }
    else { av = (a[sortKey] ?? '').toString().toLowerCase(); bv = (b[sortKey] ?? '').toString().toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paginated  = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider select-none cursor-pointer whitespace-nowrap hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200';
  const tdClass = 'px-4 py-3 text-sm text-gray-700 dark:text-gray-300';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            Email Queue
          </h1>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-0.5">
            Scheduled emails waiting to be sent
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search campaign name…"
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 w-52"
          />
          <div className="flex flex-col items-end gap-0.5">
            <button
              onClick={() => loadQueue(true)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {fetchedAt && (
              <span className="flex items-center gap-1 text-xs text-gray-400 dark:text-slate-500">
                <Clock className="h-3 w-3" />
                {(() => {
                  const mins = Math.floor((Date.now() - fetchedAt) / 60000);
                  if (mins < 1) return 'just loaded';
                  return `${mins} min${mins !== 1 ? 's' : ''} ago`;
                })()}
                {' · '}cache {cacheTtlMins}m
              </span>
            )}
          </div>
        </div>
      </div>

      {staggerError && !showStaggerModal && (
        <div className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span>{staggerError}</span>
          <button
            onClick={() => setStaggerError(null)}
            className="ml-1 p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
            title="Dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {/* Review + Mail Ops + Stagger Toolbar */}
      {(canAccess('queueReviewButtons') || canAccess('queueStaggerButton') || canAccess('metricsLogs')) && <div className="flex items-center gap-2 flex-wrap">
        {canAccess('queueReviewButtons') && <>
          <Bot className="h-4 w-4 text-indigo-500 flex-shrink-0" />
          <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Review:</span>
          <button
            onClick={() => runAiReviews('new')}
            disabled={aiProgress?.status === 'running'}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            {aiProgress?.status === 'running' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Bot className="h-3.5 w-3.5" />}
            Run New
          </button>
          <button
            onClick={() => runAiReviews('checked')}
            disabled={aiProgress?.status === 'running' || selectedJobids.size === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            Run Checked {selectedJobids.size > 0 && `(${selectedJobids.size})`}
          </button>
          <button
            onClick={() => { setRerunConfirmText(''); setShowRerunConfirm(true); }}
            disabled={aiProgress?.status === 'running' || rows.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-white dark:bg-slate-800 border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50 transition-colors"
          >
            Re-run All
          </button>

          {/* Review status filter */}
          <div className="flex items-center gap-1.5 ml-2 pl-2 border-l border-gray-200 dark:border-slate-600">
            <ListFilter className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            <select
              value={reviewFilter}
              onChange={e => handleReviewFilter(e.target.value as ReviewFilter)}
              className="text-sm rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="all">All Reviews</option>
              <option value="not_run">☐ Not Run</option>
              <option value="passed">✓ Passed</option>
              <option value="failed">! Failed</option>
              <option value="overridden">⊕ Overridden</option>
            </select>
          </div>

          {/* Live progress status */}
          {aiProgress && (
            <span className={`text-xs flex items-center gap-1 ${aiProgress.status === 'running' ? 'text-indigo-500 animate-pulse' : aiProgress.failed > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-green-600 dark:text-green-400'}`}>
              {aiProgress.status === 'running' ? (
                <>{aiProgress.completed + aiProgress.failed}/{aiProgress.total} — reviewing…</>
              ) : aiProgress.failed > 0 ? (
                <><AlertTriangle className="h-3.5 w-3.5" />{aiProgress.completed} completed, {aiProgress.failed} failed</>
              ) : (
                <>{aiProgress.total} completed, no errors</>
              )}
            </span>
          )}
          {aiStartError && (
            <span className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
              <AlertTriangle className="h-3.5 w-3.5" />{aiStartError}
            </span>
          )}
        </>}

        <div className="flex items-center gap-2 flex-wrap ml-auto">
          {canAccess('metricsLogs') && mailOpsStats && (
            <>
              <div className="hidden lg:flex items-center gap-1.5">
                <MailOpsPill
                  label="Queue"
                  value={mailOpsStats.postfix_queue_count.toLocaleString()}
                  color={queuePillColor(mailOpsStats.postfix_queue_count)}
                  tooltip={`Postfix queue: ${mailOpsStats.postfix_queue_kbytes.toLocaleString()} KB in ${mailOpsStats.postfix_queue_count.toLocaleString()} requests`}
                />
                <MailOpsPill
                  label="Rate"
                  value={formatRatePerHour(mailOpsStats.rate_60m_per_hour ?? mailOpsStats.rate_5m_per_hour)}
                  color={ratePillColor(mailOpsStats.rate_60m_per_hour ?? mailOpsStats.rate_5m_per_hour, mailOpsStats.active_jobs_count)}
                  tooltip="SMTP.com accepts in the last 60 minutes (actual emails/hour)"
                />
                <MailOpsPill
                  label="Errors"
                  value={String(mailOpsStats.serious_errors_15m)}
                  color={errorsPillColor(mailOpsStats.serious_errors_15m)}
                  tooltip="Fresh serious Postfix/SMTP errors in the last 15 minutes"
                />
              </div>
              <div className="flex lg:hidden items-center">
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium capitalize ${PILL_CLASSES[mailOpsStats.health === 'unknown' ? 'gray' : mailOpsStats.health as PillColor]}`}>
                  Mail Ops: {mailOpsStats.health}
                </span>
              </div>
            </>
          )}

          {canAccess('queueStaggerButton') && (
            <button
              onClick={openStaggerPreview}
              disabled={staggerLoading || loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
              title="Reschedule collision-day sends from 6:00 AM Pacific"
            >
              {staggerLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />}
              Stagger send times
            </button>
          )}
        </div>
      </div>}

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg text-sm text-red-700 dark:text-red-400">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-24 text-gray-400 gap-2">
          <Loader2 className="h-6 w-6 animate-spin" />
          <span>Loading queue…</span>
        </div>
      ) : !error && (
        <div className="bg-white dark:bg-slate-800 shadow-sm rounded-lg border border-gray-200 dark:border-slate-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-100 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700/60">
                <tr>
                  {/* Select checkbox column */}
                  {canAccess('queueSelectionCheckmarks') && (
                    <th className="px-3 py-3 w-8">
                      <button onClick={toggleSelectAll} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                        {selectedJobids.size > 0 && selectedJobids.size < paginated.length
                          ? <Minus className="h-4 w-4" />
                          : selectedJobids.size === paginated.length && paginated.length > 0
                            ? <CheckSquare className="h-4 w-4" />
                            : <Square className="h-4 w-4" />}
                      </button>
                    </th>
                  )}
                  {/* Review status column */}
                  {canAccess('queueReviewModal') && (
                    <th className="px-2 py-3 text-left text-xs font-semibold text-indigo-500 uppercase tracking-wider whitespace-nowrap w-8">
                      Review
                    </th>
                  )}
                  <th className={thClass} onClick={() => handleSort('campaignName')}>
                    Campaign <SortIcon col="campaignName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('listName')}>
                    List <SortIcon col="listName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('segmentName')}>
                    Segment <SortIcon col="segmentName" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th
                    className={thClass}
                    onClick={() => handleSort('createdDate')}
                    title="When this newsletter was created in Interspire"
                  >
                    Created <SortIcon col="createdDate" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('scheduledDate')}>
                    Scheduled <SortIcon col="scheduledDate" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className={thClass} onClick={() => handleSort('status')}>
                    Status <SortIcon col="status" sortKey={sortKey} sortDir={sortDir} />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-slate-700/50">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={7 + (canAccess('queueSelectionCheckmarks') ? 1 : 0) + (canAccess('queueReviewModal') ? 1 : 0)} className="px-4 py-12 text-center text-sm text-gray-400 dark:text-slate-500">
                      {search ? 'No results match your search.' : 'No emails currently in the queue.'}
                    </td>
                  </tr>
                ) : paginated.map(row => (
                  <tr key={row.jobid} className={`hover:bg-gray-50 dark:hover:bg-slate-700/30 transition-colors ${selectedJobids.has(row.jobid) ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''}`}>
                    {/* Checkbox */}
                    {canAccess('queueSelectionCheckmarks') && (
                      <td className="px-3 py-3">
                        <button onClick={() => toggleSelectJob(row.jobid)} className="text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400">
                          {selectedJobids.has(row.jobid)
                            ? <CheckSquare className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                            : <Square className="h-4 w-4" />}
                        </button>
                      </td>
                    )}
                    {/* Review status */}
                    {canAccess('queueReviewModal') && <td className="px-2 py-3">
                      <button
                        onClick={() => {
                          const rv = aiReviews[row.jobid];
                          if (rv) {
                            setAiModalReview(rv);
                            setOverrideChecked(rv.overridden ?? false);
                            setOverrideNote(rv.overrideNote ?? '');
                            setOverrideError(null);
                          }
                        }}
                        title={
                          aiReviews[row.jobid]
                            ? aiReviews[row.jobid].overridden
                              ? 'Overridden (manually approved) — click for details'
                              : aiReviews[row.jobid].passed
                                ? 'Passed — click for details'
                                : 'Failed — click for details'
                            : 'Not reviewed'
                        }
                        className="disabled:cursor-default"
                        disabled={!aiReviews[row.jobid]}
                      >
                        <AiStatusIcon review={aiReviews[row.jobid]} running={false} />
                      </button>
                    </td>}
                    <td className={`${tdClass} font-medium text-gray-900 dark:text-white max-w-xs`}>
                      <span className="truncate block" title={row.campaignName}>
                        {row.campaignName || '—'}
                      </span>
                    </td>
                    <td className={`${tdClass} text-gray-600 dark:text-gray-400`}>
                      {row.listName || <span className="text-gray-300 dark:text-slate-600">—</span>}
                    </td>
                    <td className={tdClass}>
                      {row.segmentName ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100 dark:bg-blue-900/20 dark:text-blue-300 dark:border-blue-700">
                          {row.segmentName}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-slate-600">—</span>
                      )}
                    </td>
                    <td className={`${tdClass} text-gray-500 dark:text-gray-400 whitespace-nowrap`}>
                      {formatUnixDate(row.createdDate)}
                    </td>
                    <td className={`${tdClass} text-gray-500 dark:text-gray-400 whitespace-nowrap`}>
                      <span className="inline-flex items-center gap-1.5">
                        {formatUnixDate(row.scheduledDate)}
                        {canReschedule &&
                          row.status === 'scheduled' &&
                          row.scheduledDate != null &&
                          row.scheduledDate > Math.floor(Date.now() / 1000) && (
                          <button
                            type="button"
                            onClick={() => openReschedule(row)}
                            className="inline-flex items-center justify-center p-0.5 rounded text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
                            title="Change scheduled send time"
                          >
                            <Calendar className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </span>
                    </td>
                    <td className={tdClass}>
                      <StatusBadge status={row.status} />
                    </td>
                    <td className={tdClass}>
                      <div className="flex items-center gap-1.5">
                        {/* Preview email HTML */}
                        <a
                          href={`/queue/${row.jobid}/preview${gt}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center p-1.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                          title="Preview email HTML"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </a>
                        {canClone && (
                          <button
                            type="button"
                            onClick={() => openClone(row)}
                            className="inline-flex items-center justify-center p-1.5 bg-violet-600 text-white rounded-md hover:bg-violet-700 transition-colors"
                            title="Clone campaign"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {/* Health check modal */}
                        {row.segmentId != null && (
                          <button
                            onClick={() => openHealthCheck(row)}
                            className="inline-flex items-center justify-center p-1.5 bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors"
                            title="Segment health check"
                          >
                            <Activity className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 bg-gray-50 dark:bg-slate-700/40 border-t border-gray-100 dark:border-slate-700 flex items-center justify-between gap-4">
            <span className="text-xs text-gray-400 dark:text-slate-500">
              {rows.length} job{rows.length !== 1 ? 's' : ''} in queue
            </span>
            {totalPages > 1 && (
              <div className="flex items-center gap-1.5 text-sm">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-xs text-gray-600 dark:text-gray-400 tabular-nums">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1 rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Health Check modal */}
      {healthRow && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-slate-800 dark:border-slate-600">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-3">
                <Activity className="h-5 w-5 text-emerald-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Segment Health Check</h2>
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    {healthRow.segmentName ?? `Segment ID ${healthRow.segmentId}`}
                    <span className="ml-2 text-gray-400 dark:text-slate-500">· {healthRow.campaignName}</span>
                  </p>
                </div>
              </div>
              <button onClick={closeHealthCheck} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-3">
              {healthLoading ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-500 dark:text-slate-400">
                  <RefreshCw className="h-8 w-8 animate-spin mb-3 text-emerald-600" />
                  <p className="text-sm">Counting active subscribers by location…</p>
                  <p className="text-xs text-gray-400 mt-1">This may take a moment for large lists</p>
                </div>
              ) : healthData ? (
                <>
                  <p className="text-xs text-gray-500 dark:text-slate-400 mb-3">
                    List ID: <span className="font-medium text-gray-700 dark:text-gray-200">{healthData.listId}</span>
                    &nbsp;·&nbsp; {healthData.breakdown.length} location{healthData.breakdown.length !== 1 ? 's' : ''}
                  </p>
                  <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-slate-600 max-h-[55vh] overflow-y-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-600 text-sm">
                      <thead className="bg-gray-50 dark:bg-slate-700 sticky top-0">
                        <tr>
                          <th className="px-4 py-2 text-left font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-xs">County, State</th>
                          <th className="px-4 py-2 text-right font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider text-xs">Active Contacts</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-700 bg-white dark:bg-slate-800">
                        {healthData.breakdown.map((r, idx) => (
                          <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700/40">
                            <td className="px-4 py-1.5 text-gray-800 dark:text-gray-200">{r.countyState}</td>
                            <td className="px-4 py-1.5 text-right font-medium text-gray-900 dark:text-white">{r.count.toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="bg-gray-50 dark:bg-slate-700 border-t-2 border-gray-300 dark:border-slate-500">
                          <td className="px-4 py-2 font-bold text-gray-900 dark:text-white">TOTAL</td>
                          <td className="px-4 py-2 text-right font-bold text-gray-900 dark:text-white">{healthData.total.toLocaleString()}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </>
              ) : null}
            </div>

            {healthData && !healthLoading && (
              <div className="flex items-center justify-end gap-3 px-6 py-3 border-t border-gray-200 dark:border-slate-600">
                <button
                  onClick={copyHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-500 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
                >
                  {healthCopied ? <><CheckCheck className="h-4 w-4 mr-2 text-green-600" />Copied!</> : <><Copy className="h-4 w-4 mr-2" />Copy</>}
                </button>
                <button
                  onClick={printHealthCheck}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Printer className="h-4 w-4 mr-2" />
                  Print / Save PDF
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Re-run All Confirmation Modal */}
      {showRerunConfirm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative mx-auto p-0 border w-11/12 max-w-md shadow-lg rounded-md bg-white dark:bg-slate-800 dark:border-slate-600">
            {/* Header */}
            <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-200 dark:border-slate-600 bg-amber-50 dark:bg-amber-900/20 rounded-t-md">
              <AlertCircle className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <h3 className="text-base font-semibold text-amber-800 dark:text-amber-300">Warning — Re-run All</h3>
            </div>
            {/* Body */}
            <div className="px-6 py-5 space-y-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This will re-run the AI review for <strong>all {rows.length} queued emails</strong>, including ones already reviewed. API costs will be incurred for every email.
              </p>
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Type <span className="font-mono font-bold text-red-600 dark:text-red-400">RUN</span> to confirm:
                </label>
                <input
                  type="text"
                  value={rerunConfirmText}
                  onChange={(e) => setRerunConfirmText(e.target.value)}
                  placeholder="RUN"
                  autoFocus
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-md focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-200 dark:border-slate-600">
              <button
                onClick={() => setShowRerunConfirm(false)}
                className="px-4 py-2 text-sm rounded-md border border-gray-300 dark:border-slate-500 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
              >
                Cancel
              </button>
              {rerunConfirmText === 'RUN' && (
                <button
                  onClick={() => { setShowRerunConfirm(false); runAiReviews('all'); }}
                  className="px-4 py-2 text-sm rounded-md bg-red-600 hover:bg-red-700 text-white font-medium transition-colors"
                >
                  OK — Re-run All
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Review Result Modal */}
      {aiModalReview && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-0 border w-11/12 max-w-2xl shadow-lg rounded-md bg-white dark:bg-slate-800 dark:border-slate-600">
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 dark:border-slate-600">
              <div className="flex items-center gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    Review Results — Job #{aiModalReview.jobid}
                    {aiModalReview.overridden
                      ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 flex items-center gap-1"><ShieldCheck className="h-3 w-3" />OVERRIDDEN</span>
                      : aiModalReview.passed
                        ? <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">PASSED</span>
                        : <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">FAILED</span>}
                  </h2>
                  <p className="text-xs text-gray-400 dark:text-slate-500">
                    Model: {aiModalReview.model} &nbsp;·&nbsp; Reviewed: {new Date(aiModalReview.reviewedAt).toLocaleString()}
                  </p>
                </div>
              </div>
              <button onClick={() => setAiModalReview(null)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-4 max-h-[50vh] overflow-y-auto space-y-2">
              {aiModalReview.results.map((r, idx) => (
                <div key={idx} className={`flex items-start gap-3 rounded-lg p-3 border ${r.passed ? 'border-green-100 bg-green-50 dark:border-green-700/30 dark:bg-green-900/10' : 'ai-rule-fail'}`}>
                  <div className="flex-shrink-0 mt-0.5">
                    {r.passed
                      ? <CheckCircle className="h-4 w-4 text-green-500" />
                      : <AlertCircle className="h-4 w-4 text-red-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{r.rule}</p>
                    {r.note && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.note}</p>}
                    {r.evidence && r.evidence.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {r.evidence.map((snippet, si) => (
                          <code key={si} className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                            r.passed
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                          }`}>
                            {snippet}
                          </code>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {aiModalReview.results.length === 0 && (
                <p className="text-sm text-gray-400 italic text-center py-8">No rule results returned.</p>
              )}
            </div>

            {/* Override section */}
            <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/40 space-y-2">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={overrideChecked}
                  onChange={(e) => setOverrideChecked(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 dark:border-slate-500 text-teal-600 accent-teal-500 focus:ring-teal-500"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-1.5">
                  <ShieldCheck className="h-3.5 w-3.5 text-teal-500" />
                  Override — mark as passed despite failing rules
                </span>
              </label>
              {overrideChecked && (
                <input
                  type="text"
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  placeholder="Reason (optional, e.g. one-off send, intentional variation)"
                  maxLength={500}
                  className="w-full text-sm border border-gray-300 dark:border-slate-500 rounded-md px-3 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-teal-500"
                />
              )}
              {overrideError && (
                <p className="text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                  <AlertTriangle className="h-3.5 w-3.5" />{overrideError}
                </p>
              )}
            </div>

            <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-600 flex justify-between items-center">
              <button
                onClick={handleOverrideSave}
                disabled={overrideSaving || (overrideChecked === (aiModalReview.overridden ?? false) && overrideNote === (aiModalReview.overrideNote ?? ''))}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700 disabled:opacity-40 transition-colors"
              >
                {overrideSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                {overrideSaving ? 'Saving…' : 'Save Override'}
              </button>
              <button
                onClick={() => setAiModalReview(null)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-500 text-sm font-medium rounded-md text-gray-700 dark:text-gray-200 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Stagger preview / apply modal */}
      {showStaggerModal && staggerPreview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-600 flex justify-between items-start">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <CalendarClock className="h-5 w-5 text-amber-600" />
                  Stagger send times
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1">
                  {staggerPreview.changes.length} job(s) on {staggerPreview.daysAffected} day(s) with hour collisions.
                  Slots start at 6:00 AM Pacific. Select which jobs to reschedule.
                </p>
              </div>
              <button
                onClick={() => { setShowStaggerModal(false); setStaggerPreview(null); setStaggerError(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {staggerPreview.changes.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-slate-400">
                No collision-day sends need staggering right now.
              </div>
            ) : (
              <>
                <div className="px-6 py-2 flex items-center gap-3 border-b border-gray-100 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => setStaggerSelected(new Set(staggerPreview.changes.map((c) => c.jobid)))}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    onClick={() => setStaggerSelected(new Set())}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Clear
                  </button>
                  <span className="text-xs text-gray-500 ml-auto">{staggerSelected.size} selected</span>
                </div>
                <div className="overflow-auto flex-1 px-6 py-2">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 uppercase">
                        <th className="py-2 w-8" />
                        <th className="py-2 pr-4">Campaign</th>
                        <th className="py-2 pr-4">Date</th>
                        <th className="py-2 pr-4">Current</th>
                        <th className="py-2">New time</th>
                      </tr>
                    </thead>
                    <tbody>
                      {staggerPreview.changes.map((c) => (
                        <tr key={c.jobid} className="border-t border-gray-100 dark:border-slate-700">
                          <td className="py-2">
                            <input
                              type="checkbox"
                              checked={staggerSelected.has(c.jobid)}
                              onChange={(e) => {
                                setStaggerSelected((prev) => {
                                  const next = new Set(prev);
                                  if (e.target.checked) next.add(c.jobid);
                                  else next.delete(c.jobid);
                                  return next;
                                });
                              }}
                              className="h-4 w-4 rounded"
                            />
                          </td>
                          <td className="py-2 pr-4 text-gray-800 dark:text-gray-200 max-w-xs truncate">{c.campaignName}</td>
                          <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{c.pacificDate}</td>
                          <td className="py-2 pr-4 text-gray-600 dark:text-gray-400 whitespace-nowrap">{formatUnixDate(c.oldScheduledDate)}</td>
                          <td className="py-2 text-amber-700 dark:text-amber-300 whitespace-nowrap font-medium">{formatUnixDate(c.newScheduledDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {staggerPreview.warnings.length > 0 && (
                  <div className="px-6 py-2 text-xs text-amber-700 dark:text-amber-300 border-t border-gray-100 dark:border-slate-700">
                    {staggerPreview.warnings.map((w, i) => (
                      <p key={i}>{w}</p>
                    ))}
                  </div>
                )}
              </>
            )}

            {staggerError && (
              <p className="px-6 text-sm text-red-600 dark:text-red-400">{staggerError}</p>
            )}

            <div className="px-6 py-3 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                onClick={() => { setShowStaggerModal(false); setStaggerPreview(null); setStaggerError(null); }}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-md text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={applyStaggerSelected}
                disabled={staggerApplying || staggerSelected.size === 0 || staggerPreview.changes.length === 0}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
              >
                {staggerApplying && <Loader2 className="h-4 w-4 animate-spin" />}
                Apply selected ({staggerSelected.size})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single-job reschedule modal */}
      {rescheduleRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-600 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-blue-600" />
                  Change send time
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1 truncate" title={rescheduleRow.campaignName}>
                  {rescheduleRow.campaignName || `Job #${rescheduleRow.jobid}`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeReschedule}
                disabled={rescheduleSaving}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Scheduled date &amp; time
                <input
                  type="datetime-local"
                  value={rescheduleValue}
                  onChange={(e) => setRescheduleValue(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Only future times are allowed. Currently sending jobs cannot be changed.
              </p>
              {rescheduleError && (
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {rescheduleError}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReschedule}
                disabled={rescheduleSaving}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-md text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveReschedule()}
                disabled={rescheduleSaving || !rescheduleValue}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {rescheduleSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone campaign modal */}
      {cloneRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-600 flex justify-between items-start gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Copy className="h-5 w-5 text-violet-600" />
                  Clone campaign
                </h2>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1 truncate" title={cloneRow.campaignName}>
                  From: {cloneRow.campaignName || `Job #${cloneRow.jobid}`}
                </p>
              </div>
              <button
                type="button"
                onClick={closeClone}
                disabled={cloneSaving}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 shrink-0"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Campaign name
                <input
                  type="text"
                  value={cloneName}
                  onChange={(e) => setCloneName(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Scheduled date &amp; time
                <input
                  type="datetime-local"
                  value={cloneWhen}
                  onChange={(e) => setCloneWhen(e.target.value)}
                  className="mt-1 block w-full rounded-md border border-gray-300 dark:border-slate-500 bg-white dark:bg-slate-700 text-gray-900 dark:text-white px-3 py-2 text-sm"
                />
              </label>
              <p className="text-xs text-gray-500 dark:text-slate-400">
                Copies the full email (subject, HTML, list/segment). Only the name and schedule are set here.
              </p>
              {cloneError && (
                <p className="text-sm text-red-600 dark:text-red-400 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {cloneError}
                </p>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-600 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeClone}
                disabled={cloneSaving}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-500 rounded-md text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveClone()}
                disabled={cloneSaving || !cloneName.trim() || !cloneWhen}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50"
              >
                {cloneSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Clone
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
